import { fetchWrapper } from '@/reducers/fetch'
import { adminApiAxios } from '@/api'
import type { PreparerAmountSource } from '@/features/taxes/annual-taxes/annualTaxOutcome.slice'

export enum TaxfyleJobHistoryFormType {
  form_1040 = 'form_1040',
  form_1120_s = 'form_1120_s',
  form_4868 = 'form_4868',
  form_7004 = 'form_7004',
}

export enum TaxfyleJobHistoryChangedField {
  jobStatus = 'jobStatus',
  jobStep = 'jobStep',
  services = 'services',
  members = 'members',
  transferredProviderReason = 'transferredProviderReason',
  relationshipManager = 'relationshipManager',
  preparerAmount = 'preparerAmount',
  totalCost = 'totalCost',
}

export interface TaxfyleJobHistoryMember {
  role: 'CHAMPION' | 'SUPPORTING'
  type: 'CLIENT' | 'PROVIDER'
  user_id: string
  given_name: string
  family_name: string
  display_name: string
}

export const isTaxfyleJobHistoryMember = (
  value: unknown
): value is TaxfyleJobHistoryMember => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'role' in value &&
    'type' in value &&
    'user_id' in value &&
    'given_name' in value &&
    'family_name' in value &&
    'display_name' in value
  )
}

export interface TaxfyleJobHistoryRelationshipManager {
  user_id: string
  first_name: string | null
  last_name: string | null
  full_name: string | null
  email: string | null
  meeting_link: string | null
  phone_number: string | null
}

export const isTaxfyleJobHistoryRelationshipManager = (
  value: unknown
): value is TaxfyleJobHistoryRelationshipManager =>
  typeof value === 'object' &&
  value !== null &&
  'user_id' in value &&
  'full_name' in value &&
  'meeting_link' in value

export interface TaxfyleServiceChange {
  service: string
  quantity: number | null
  listPriceInCents: number | null
  scopeChangeId: string | null
}

export const isTaxfyleServiceChange = (
  value: unknown
): value is TaxfyleServiceChange =>
  typeof value === 'object' &&
  value !== null &&
  'service' in value &&
  'quantity' in value &&
  'listPriceInCents' in value &&
  'scopeChangeId' in value

export interface TaxfyleJobHistoryPreparerAmount {
  jurisdiction: string
  isRefund: boolean | null
  amountInCents: number | null
  source: PreparerAmountSource | null
}

export const isTaxfyleJobHistoryPreparerAmount = (
  value: unknown
): value is TaxfyleJobHistoryPreparerAmount =>
  typeof value === 'object' &&
  value !== null &&
  'jurisdiction' in value &&
  'isRefund' in value &&
  'amountInCents' in value &&
  'source' in value

type TaxfyleJobHistoryValue =
  | string
  | number
  | TaxfyleJobHistoryMember
  | TaxfyleJobHistoryRelationshipManager
  | TaxfyleJobHistoryPreparerAmount
  | TaxfyleServiceChange
  | null

export interface TaxfyleJobHistory {
  id: number
  jobId: string
  formType: TaxfyleJobHistoryFormType | null
  changedField: TaxfyleJobHistoryChangedField
  oldValue: TaxfyleJobHistoryValue
  newValue: TaxfyleJobHistoryValue
  createdAt: string
  updatedAt: string
}

export const getAdminTaxfyleJobHistoryKey = (jobId: string) =>
  `FETCH_ADMIN_TAXFYLE_JOB_HISTORY_KEY_${jobId}`
export const fetchTaxfyleJobHistory = (jobId: string) =>
  fetchWrapper({
    fetchKey: getAdminTaxfyleJobHistoryKey(jobId),
    fetchFunction: async () => {
      const json = await adminApiAxios.get<TaxfyleJobHistory[]>(
        `/finances/api/v2/admin/taxfyle_job_history/${jobId}`
      )
      return json.data
    },
  })
